import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Settings,
  Phone,
  Building2,
  Save,
  RefreshCw,
  ArrowLeft,
  CheckCircle,
  AlertCircle,
  Briefcase,
  Wrench,
  Stethoscope,
  UtensilsCrossed,
  Car,
  HelpCircle
} from 'lucide-react';
import { API_BASE_URL } from '../config';
import LoadingSpinner from '../components/LoadingSpinner';
import toast from 'react-hot-toast';
import clsx from 'clsx';

// Business type presets with icons
const businessPresets = {
  hvac: {
    name: 'HVAC Services',
    icon: Wrench,
    businessType: 'hvac',
    businessRole: 'receptionist',
    businessGreeting: "Thank you for calling {{businessName}}, your trusted heating and cooling experts. How can I help you today?",
    businessPrompt: `You are the receptionist at an HVAC company. You handle:
- Scheduling service appointments for heating and AC repairs
- Emergency HVAC calls (prioritize these)
- Questions about services (installation, maintenance, repair)
- Providing rough price estimates when asked
- Taking down customer information for callbacks

Be professional, helpful, and knowledgeable about HVAC services. If asked about specific pricing, provide ranges and offer to have a technician call back with exact quotes.`,
    businessServices: [
      'AC Repair & Installation',
      'Heating System Repair',
      'Furnace Maintenance',
      'Ductwork Services',
      'Emergency 24/7 Service',
      'Preventive Maintenance Plans'
    ],
    businessHours: 'Mon-Fri 8am-6pm, Emergency service 24/7'
  },
  plumbing: {
    name: 'Plumbing Services',
    icon: Wrench,
    businessType: 'plumbing',
    businessRole: 'receptionist',
    businessGreeting: "Thanks for calling {{businessName}}. We're here to help with all your plumbing needs. What can I do for you?",
    businessPrompt: `You are the receptionist at a plumbing company. You handle:
- Scheduling plumbing service calls
- Emergency water leak and pipe burst calls (highest priority)
- Questions about services (drain cleaning, water heater, pipe repair)
- Providing service area information
- Taking customer details for appointments

Be calm, professional, and reassuring, especially for emergency calls.`,
    businessServices: [
      'Drain Cleaning',
      'Water Heater Repair & Installation',
      'Pipe Repair & Replacement',
      'Leak Detection',
      'Toilet & Faucet Repair',
      'Sewer Line Services',
      'Emergency Plumbing 24/7'
    ],
    businessHours: 'Mon-Sat 7am-7pm, Emergency service 24/7'
  },
  medical: {
    name: 'Medical Office',
    icon: Stethoscope,
    businessType: 'medical',
    businessRole: 'receptionist',
    businessGreeting: "Thank you for calling {{businessName}}. How may I assist you today?",
    businessPrompt: `You are the receptionist at a medical office. You handle:
- Scheduling patient appointments
- Prescription refill requests (take message for doctor)
- General inquiries about office hours and services
- Insurance and billing questions (transfer to billing if complex)
- Urgent medical concerns (advise to call 911 for emergencies)

Be professional, HIPAA-conscious, and compassionate. Never give medical advice.`,
    businessServices: [
      'Primary Care Visits',
      'Annual Physicals',
      'Sick Visits',
      'Vaccination Services',
      'Lab Work',
      'Referral Coordination'
    ],
    businessHours: 'Mon-Fri 8am-5pm'
  },
  restaurant: {
    name: 'Restaurant',
    icon: UtensilsCrossed,
    businessType: 'restaurant',
    businessRole: 'host',
    businessGreeting: "Thank you for calling {{businessName}}! How can I help you today?",
    businessPrompt: `You are the host at a restaurant. You handle:
- Taking reservations (date, time, party size, special requests)
- Answering questions about the menu and dietary accommodations
- Providing hours of operation and location info
- Handling large party/event inquiries
- Takeout and delivery questions

Be friendly, upbeat, and welcoming. Know the restaurant's specialties.`,
    businessServices: [
      'Dine-in Reservations',
      'Private Events',
      'Takeout Orders',
      'Catering Services',
      'Gift Cards'
    ],
    businessHours: 'Tue-Sun 11am-10pm, Closed Mondays'
  },
  auto: {
    name: 'Auto Repair Shop',
    icon: Car,
    businessType: 'auto',
    businessRole: 'service_advisor',
    businessGreeting: "Thanks for calling {{businessName}}. How can we help you with your vehicle today?",
    businessPrompt: `You are a service advisor at an auto repair shop. You handle:
- Scheduling vehicle service appointments
- Discussing vehicle symptoms and concerns
- Providing rough estimates when possible
- Towing and roadside assistance coordination
- Service status updates

Be knowledgeable about common car issues, professional, and helpful.`,
    businessServices: [
      'Oil Changes',
      'Brake Service',
      'Engine Diagnostics',
      'Tire Services',
      'AC Repair',
      'Transmission Service',
      'State Inspections'
    ],
    businessHours: 'Mon-Fri 7:30am-6pm, Sat 8am-2pm'
  },
  general: {
    name: 'General Business',
    icon: Briefcase,
    businessType: 'general',
    businessRole: 'receptionist',
    businessGreeting: "Thank you for calling {{businessName}}. How may I direct your call?",
    businessPrompt: `You are a professional receptionist. You handle:
- Answering general inquiries about the business
- Taking messages for staff members
- Scheduling appointments when applicable
- Providing business hours and location
- Directing calls to appropriate departments

Be professional, courteous, and helpful.`,
    businessServices: [],
    businessHours: 'Mon-Fri 9am-5pm'
  }
};

const QAConfigPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [config, setConfig] = useState({
    testerPhoneNumber: '',
    mockTransferNumber: '',
    defaultVoice: 'alloy',
    isActive: true,
    // Business simulation
    inboundMode: 'customer',
    businessName: '',
    businessType: '',
    businessRole: 'receptionist',
    businessGreeting: '',
    businessPrompt: '',
    businessServices: [],
    businessHours: '',
    businessBehavior: 'helpful',
    // Call scenario simulation
    simulateHold: false,
    holdDurationSeconds: 15,
    simulateIvrMenu: false,
    ivrMenuPrompt: '',
    simulateAnswerDelay: false,
    answerDelaySeconds: 10
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      // Fetch phone numbers and config in parallel
      const [phonesRes, configRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/phone/my-numbers`, { headers }),
        fetch(`${API_BASE_URL}/api/qa/config`, { headers })
      ]);

      if (phonesRes.ok) {
        const data = await phonesRes.json();
        // The endpoint returns { success: true, data: [...] }
        setPhoneNumbers(data.data || data.numbers || data.phoneNumbers || []);
      }

      if (configRes.ok) {
        const data = await configRes.json();
        if (data.config) {
          setConfig({
            testerPhoneNumber: data.config.testerPhoneNumber || '',
            mockTransferNumber: data.config.mockTransferNumber || '',
            defaultVoice: data.config.defaultVoice || 'alloy',
            isActive: data.config.isActive ?? true,
            inboundMode: data.config.inboundMode || 'customer',
            businessName: data.config.businessName || '',
            businessType: data.config.businessType || '',
            businessRole: data.config.businessRole || 'receptionist',
            businessGreeting: data.config.businessGreeting || '',
            businessPrompt: data.config.businessPrompt || '',
            businessServices: data.config.businessServices || [],
            businessHours: data.config.businessHours || '',
            businessBehavior: data.config.businessBehavior || 'helpful',
            simulateHold: data.config.simulateHold || false,
            holdDurationSeconds: data.config.holdDurationSeconds || 15,
            simulateIvrMenu: data.config.simulateIvrMenu || false,
            ivrMenuPrompt: data.config.ivrMenuPrompt || '',
            simulateAnswerDelay: data.config.simulateAnswerDelay || false,
            answerDelaySeconds: data.config.answerDelaySeconds || 10
          });
        }
      }
    } catch (error) {
      console.error('Failed to fetch config:', error);
      toast.error('Failed to load configuration');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handlePresetSelect = (presetKey) => {
    const preset = businessPresets[presetKey];
    if (preset) {
      setConfig(prev => ({
        ...prev,
        businessType: preset.businessType,
        businessRole: preset.businessRole,
        businessGreeting: preset.businessGreeting.replace('{{businessName}}', prev.businessName || 'our company'),
        businessPrompt: preset.businessPrompt,
        businessServices: preset.businessServices,
        businessHours: preset.businessHours
      }));
      toast.success(`Applied ${preset.name} preset`);
    }
  };

  const handleSave = async () => {
    if (!config.testerPhoneNumber) {
      toast.error('Please select a tester phone number');
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/api/qa/config`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(config)
      });

      if (response.ok) {
        toast.success('QA configuration saved successfully');
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to save configuration');
      }
    } catch (error) {
      console.error('Failed to save config:', error);
      toast.error('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/app/qa"
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">QA Tester Configuration</h1>
            <p className="text-gray-600 mt-1">
              Configure how the QA tester handles inbound and outbound calls
            </p>
          </div>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50"
        >
          {saving ? (
            <>
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Save Configuration
            </>
          )}
        </button>
      </div>

      {/* Phone Configuration */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Phone className="w-5 h-5 mr-2" />
          Phone Configuration
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tester Phone Number *
            </label>
            <select
              value={config.testerPhoneNumber}
              onChange={(e) => setConfig({ ...config, testerPhoneNumber: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
            >
              <option value="">Select a phone number</option>
              {phoneNumbers.map(phone => (
                <option key={phone.id} value={phone.phoneNumber}>
                  {phone.phoneNumber} {phone.label && `(${phone.label})`}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              This number will be used for QA testing
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Mock Transfer Number
            </label>
            <input
              type="tel"
              value={config.mockTransferNumber}
              onChange={(e) => setConfig({ ...config, mockTransferNumber: e.target.value })}
              placeholder="+1234567890"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">
              Number for testing call transfers
            </p>
          </div>

          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg md:col-span-2">
            <div>
              <p className="font-medium text-gray-900">QA Tester Active</p>
              <p className="text-sm text-gray-600">Enable or disable the QA tester</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={config.isActive}
                onChange={(e) => setConfig({ ...config, isActive: e.target.checked })}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-gray-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
            </label>
          </div>
        </div>
      </div>

      {/* Inbound Mode Selection */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Settings className="w-5 h-5 mr-2" />
          Inbound Call Mode
        </h2>
        <p className="text-gray-600 mb-4">
          Choose how the QA tester should behave when receiving inbound calls
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button
            type="button"
            onClick={() => setConfig({ ...config, inboundMode: 'customer' })}
            className={clsx(
              'p-4 rounded-lg border-2 text-left transition-all',
              config.inboundMode === 'customer'
                ? 'border-black bg-gray-50'
                : 'border-gray-200 hover:border-gray-300'
            )}
          >
            <div className="flex items-center gap-3 mb-2">
              <div className={clsx(
                'w-10 h-10 rounded-full flex items-center justify-center',
                config.inboundMode === 'customer' ? 'bg-black text-white' : 'bg-gray-100'
              )}>
                <HelpCircle className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Customer Persona</h3>
                <p className="text-sm text-gray-600">Answer as a potential customer</p>
              </div>
            </div>
            <p className="text-sm text-gray-500 mt-2">
              Use the answering personas defined in test suites (skeptical buyer, busy person, etc.) to test your sales AI
            </p>
          </button>

          <button
            type="button"
            onClick={() => setConfig({ ...config, inboundMode: 'business' })}
            className={clsx(
              'p-4 rounded-lg border-2 text-left transition-all',
              config.inboundMode === 'business'
                ? 'border-black bg-gray-50'
                : 'border-gray-200 hover:border-gray-300'
            )}
          >
            <div className="flex items-center gap-3 mb-2">
              <div className={clsx(
                'w-10 h-10 rounded-full flex items-center justify-center',
                config.inboundMode === 'business' ? 'bg-black text-white' : 'bg-gray-100'
              )}>
                <Building2 className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Business Simulation</h3>
                <p className="text-sm text-gray-600">Answer as a business (HVAC, medical, etc.)</p>
              </div>
            </div>
            <p className="text-sm text-gray-500 mt-2">
              Simulate being a real business to test how your sales pipeline handles calls to different business types
            </p>
          </button>
        </div>
      </div>

      {/* Business Simulation Settings */}
      {config.inboundMode === 'business' && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Building2 className="w-5 h-5 mr-2" />
            Business Simulation Settings
          </h2>

          {/* Business Type Presets */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Quick Start: Select a Business Type
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {Object.entries(businessPresets).map(([key, preset]) => {
                const Icon = preset.icon;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => handlePresetSelect(key)}
                    className={clsx(
                      'p-3 rounded-lg border text-center transition-all hover:bg-gray-50',
                      config.businessType === key
                        ? 'border-black bg-gray-50'
                        : 'border-gray-200'
                    )}
                  >
                    <Icon className="w-6 h-6 mx-auto mb-2 text-gray-600" />
                    <span className="text-sm font-medium">{preset.name}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Business Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Business Name *
              </label>
              <input
                type="text"
                value={config.businessName}
                onChange={(e) => setConfig({ ...config, businessName: e.target.value })}
                placeholder="Smith's HVAC Services"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Role
              </label>
              <select
                value={config.businessRole}
                onChange={(e) => setConfig({ ...config, businessRole: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
              >
                <option value="receptionist">Receptionist</option>
                <option value="owner">Business Owner</option>
                <option value="manager">Manager</option>
                <option value="technician">Technician</option>
                <option value="host">Host</option>
                <option value="service_advisor">Service Advisor</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Business Hours
              </label>
              <input
                type="text"
                value={config.businessHours}
                onChange={(e) => setConfig({ ...config, businessHours: e.target.value })}
                placeholder="Mon-Fri 8am-5pm"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Response Behavior
              </label>
              <select
                value={config.businessBehavior}
                onChange={(e) => setConfig({ ...config, businessBehavior: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
              >
                <option value="helpful">Helpful & Professional</option>
                <option value="busy">Busy (short responses)</option>
                <option value="skeptical">Skeptical of sales calls</option>
                <option value="interested">Interested in services</option>
              </select>
            </div>
          </div>

          {/* Greeting */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Greeting Message
            </label>
            <textarea
              value={config.businessGreeting}
              onChange={(e) => setConfig({ ...config, businessGreeting: e.target.value })}
              rows={2}
              placeholder="Thank you for calling our company. How can I help you today?"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
            />
          </div>

          {/* AI Prompt */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              AI Instructions
            </label>
            <textarea
              value={config.businessPrompt}
              onChange={(e) => setConfig({ ...config, businessPrompt: e.target.value })}
              rows={8}
              placeholder="Describe how the AI should behave as this business..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent font-mono text-sm"
            />
            <p className="text-xs text-gray-500 mt-1">
              Detailed instructions for the AI on how to act as this business
            </p>
          </div>

          {/* Services */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Services Offered
            </label>
            <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-lg min-h-[60px]">
              {(config.businessServices || []).map((service, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-3 py-1 bg-white border border-gray-200 rounded-full text-sm"
                >
                  {service}
                  <button
                    type="button"
                    onClick={() => {
                      const newServices = [...config.businessServices];
                      newServices.splice(index, 1);
                      setConfig({ ...config, businessServices: newServices });
                    }}
                    className="ml-2 text-gray-400 hover:text-gray-600"
                  >
                    ×
                  </button>
                </span>
              ))}
              <input
                type="text"
                placeholder="Add service..."
                className="flex-1 min-w-[150px] px-2 py-1 border-none bg-transparent focus:outline-none"
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && e.target.value.trim()) {
                    setConfig({
                      ...config,
                      businessServices: [...(config.businessServices || []), e.target.value.trim()]
                    });
                    e.target.value = '';
                  }
                }}
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Press Enter to add a service
            </p>
          </div>
        </div>
      )}

      {/* Call Scenario Simulation - Only visible in business mode */}
      {config.inboundMode === 'business' && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">
            Call Scenario Simulation
          </h2>
          <p className="text-gray-600 mb-6 text-sm">
            Test how your Sales Pipeline AI handles real-world call scenarios like hold times, IVR menus, and delayed answers.
          </p>

          <div className="space-y-6">
            {/* Simulate Answer Delay */}
            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <h3 className="font-medium text-gray-900">Simulate Answer Delay</h3>
                  <p className="text-sm text-gray-600">
                    AI stays silent for a set duration before answering, simulating ring time
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={config.simulateAnswerDelay}
                    onChange={(e) => setConfig({ ...config, simulateAnswerDelay: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-gray-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
                </label>
              </div>
              {config.simulateAnswerDelay && (
                <div className="mt-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Delay Duration (seconds)
                  </label>
                  <input
                    type="number"
                    min="5"
                    max="60"
                    value={config.answerDelaySeconds}
                    onChange={(e) => setConfig({ ...config, answerDelaySeconds: parseInt(e.target.value) || 10 })}
                    className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 mt-1">Range: 5-60 seconds</p>
                </div>
              )}
            </div>

            {/* Simulate IVR Menu */}
            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <h3 className="font-medium text-gray-900">Simulate IVR Menu</h3>
                  <p className="text-sm text-gray-600">
                    AI presents an automated phone menu the caller must navigate before speaking to a person
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={config.simulateIvrMenu}
                    onChange={(e) => setConfig({ ...config, simulateIvrMenu: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-gray-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
                </label>
              </div>
              {config.simulateIvrMenu && (
                <div className="mt-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    IVR Menu Prompt
                  </label>
                  <textarea
                    value={config.ivrMenuPrompt}
                    onChange={(e) => setConfig({ ...config, ivrMenuPrompt: e.target.value })}
                    rows={3}
                    placeholder="Press 1 for appointments, press 2 for billing, press 0 for operator"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent text-sm"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    The menu options the AI will read to the caller
                  </p>
                </div>
              )}
            </div>

            {/* Simulate Hold */}
            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <h3 className="font-medium text-gray-900">Simulate Hold</h3>
                  <p className="text-sm text-gray-600">
                    AI puts the caller on hold mid-conversation, then returns
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={config.simulateHold}
                    onChange={(e) => setConfig({ ...config, simulateHold: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-gray-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
                </label>
              </div>
              {config.simulateHold && (
                <div className="mt-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Hold Duration (seconds)
                  </label>
                  <input
                    type="number"
                    min="5"
                    max="120"
                    value={config.holdDurationSeconds}
                    onChange={(e) => setConfig({ ...config, holdDurationSeconds: parseInt(e.target.value) || 15 })}
                    className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 mt-1">Range: 5-120 seconds</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <h3 className="font-medium text-blue-900">How Business Simulation Works</h3>
            <p className="text-sm text-blue-700 mt-1">
              When an external caller (like your sales pipeline) calls your QA tester number,
              the AI will answer as if it were the configured business. This lets you test how
              well your sales AI handles calls to different types of businesses without calling
              real companies.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QAConfigPage;
